import axios from 'axios';
import { buildBiosdkNistRouteUrl } from './biosdkRoutes';
import { bdbToUint8Array, isBchiTemplate } from './bdbUtils';
import {
  buildIrisExtractInnerRequest,
  buildIrisMatchInnerRequest,
  buildIrisProcessedSegment,
  buildIrisRawSegment,
  fileToRawBdbBytes,
  wrapEncodedRequest,
  wrapMatchRequest,
} from './nistRequestBuilder';
import {
  NIST_IRIS_MODE_FLAG,
  irisScenarioToModeFlag,
} from './nistConstants';
import { resolveIrisGalleryBdb, isPlainImageFile } from './nistGalleryFile';
import { MODALITY_IRIS, pickBestGalleryMatch, rankGalleryDecisions } from './nistMatchRank';
import { parseExtractRecordMeta, parseExtractSegments, parseMatchDecisions } from './nistResponseParser';

export { pickBestGalleryMatch as pickBestIrisGalleryMatch, rankGalleryDecisions };

export async function extractNistIrisTemplates(biosdkBaseUrl, files, scenario = 'single', irisSubtype = 'Left') {
  if (!files?.length) throw new Error('No images provided for NIST iris extract.');

  const segments = [];
  for (const file of files) {
    const bytes = await fileToRawBdbBytes(file);
    segments.push(buildIrisRawSegment(bytes, irisSubtype, 'ENROLL'));
  }

  const flags = {
    [NIST_IRIS_MODE_FLAG]: irisScenarioToModeFlag(scenario),
  };

  const inner = buildIrisExtractInnerRequest({ segments, flags });
  const body = await wrapEncodedRequest(inner);
  const url = buildBiosdkNistRouteUrl(biosdkBaseUrl, 'extract-template');
  const extractResponse = await axios.post(url, body, {
    headers: { 'Content-Type': 'application/json' },
  });

  const parsed = parseExtractSegments(extractResponse.data).filter((seg) => seg.bdbBytes?.length > 0);
  if (!parsed.length) throw new Error('No BDB in iris extract response');
  const meta = parseExtractRecordMeta(extractResponse.data);
  return { templates: parsed, meta, scenario };
}

export async function matchNistIrisTemplates(biosdkBaseUrl, probeBdbBytes, galleryBdbBytes, irisSubtype = 'Left') {
  const galleryList = Array.isArray(galleryBdbBytes) ? galleryBdbBytes : [galleryBdbBytes];
  const probeSegment = buildIrisProcessedSegment(probeBdbBytes, irisSubtype, 'VERIFY');
  const gallerySegments = galleryList.map((bdb) => buildIrisProcessedSegment(bdb, irisSubtype, 'VERIFY'));
  const inner = buildIrisMatchInnerRequest({
    probeSegment,
    gallerySegments,
    probeLevel: 'PROCESSED',
    galleryLevel: 'PROCESSED',
  });
  const finalBody = await wrapMatchRequest(inner);
  const url = buildBiosdkNistRouteUrl(biosdkBaseUrl, 'match');
  return axios.post(url, finalBody, { headers: { 'Content-Type': 'application/json' } });
}

async function resolveProbeBdb(biosdkBaseUrl, probeFile, scenario, irisSubtype) {
  if (isPlainImageFile(probeFile)) {
    const extracted = await extractNistIrisTemplates(biosdkBaseUrl, [probeFile], scenario, irisSubtype);
    const probeBdb = extracted.templates[0]?.bdbBytes;
    if (!probeBdb?.length) {
      throw new Error('Probe template is empty after extract.');
    }
    return probeBdb;
  }
  return bdbToUint8Array(await probeFile.arrayBuffer());
}

export async function matchNistIrisWithGalleryFiles(
  biosdkBaseUrl,
  probeFile,
  galleryFiles,
  scenario = 'single',
  irisSubtype = 'Left'
) {
  const probeBdb = await resolveProbeBdb(biosdkBaseUrl, probeFile, scenario, irisSubtype);
  if (!probeBdb?.length) {
    throw new Error('Probe template is empty.');
  }

  const galleryBdbs = [];
  for (const gf of galleryFiles) {
    galleryBdbs.push(await resolveIrisGalleryBdb(biosdkBaseUrl, gf, scenario, irisSubtype));
  }

  const matchResponse = await matchNistIrisTemplates(biosdkBaseUrl, probeBdb, galleryBdbs, irisSubtype);
  const decisions = parseMatchDecisions(matchResponse.data, MODALITY_IRIS);
  return { probeBdb, matchResponse, decisions, probeIsBchi: isBchiTemplate(probeBdb) };
}
